import Swiper from 'Swiper';
import { smoothScrollTo, getOffset } from '../../js/helper';
import stickybits from 'stickybits';

class ModelFinderView {
    constructor ($modelFinder, options) {
        this.breakpoints = options.breakpoints;
        this.modelFinderTracking = options.modelFinderTracking;
        this.clickedStepNumber = 0; // get updated by controller
        this.isFinished = false;
        this.slider = undefined;

        this.filterStickybits = undefined;
        this.resultStickybits = undefined;

        this.onUpdate = options.onUpdate;

        // filter DOM elements
        this.$modelFinder = $modelFinder;
        this.$filter = $modelFinder.querySelector('[data-model-finder="filter"]');
        this.$$steps = $modelFinder.querySelectorAll('[data-model-finder="step"]');
        this.$preFinishText = $modelFinder.querySelector('[data-model-finder="pre-finish-text"]');
        this.$finishText = $modelFinder.querySelector('[data-model-finder="finish-text"]');
        this.$resetButton = $modelFinder.querySelector('[data-model-finder="reset"]');

        // result DOM elements
        this.$result = $modelFinder.querySelector('[data-model-finder="result"]');
    }

    handleStickybits () {
        const removeStickybits = (stickybitsInstance) => {
            if (stickybitsInstance !== undefined && typeof stickybitsInstance.cleanup === 'function') {
                stickybitsInstance.cleanup();
            }
        };

        if (window.Modernizr.ie11 && window.innerWidth >= this.breakpoints.l) {
            const isResultActive = this.$modelFinder.classList.contains('model-finder--active');

            if (isResultActive) {
                removeStickybits(this.resultStickybits);
                this.filterStickybits = stickybits(this.$filter);
            } else {
                removeStickybits(this.filterStickybits);
                this.resultStickybits = stickybits(this.$result);
            }
        } else {
            removeStickybits(this.resultStickybits);
            removeStickybits(this.filterStickybits);
        }
    }

    /**
     * check if the product is already finished on page load
     * then activate the overlay, but only on desktop
     */
    checkForInitialFinishedConfig () {
        let isFinished = true;
        for (let i = 0; i < this.$$steps.length; i++) {
            const $step = this.$$steps[i];

            if (!$step.classList.contains('model-finder__step--filled')) {
                isFinished = false;
            }
        }

        if (isFinished) {
            this.$modelFinder.classList.add('model-finder--active');
            this.unlockSlider();
        }
    }

    /**
     * ##################################################
     *
     * filter related DOM manipulation
     *
     * ##################################################
     */

    setTextForOptionPlaceholder (stepNumber) {
        const $activeStep = this.$$steps[stepNumber - 1];
        const $optionPlaceholder = $activeStep.querySelector('[data-model-finder="option-placeholder"]');
        const $selectedOptionLabel = $activeStep.querySelector('[data-model-finder="radio"]:checked + [data-model-finder="label"]');

        $activeStep.classList.remove('model-finder__step--active');
        $activeStep.classList.add('model-finder__step--filled');
        if ($optionPlaceholder && $selectedOptionLabel) {
            $optionPlaceholder.innerHTML = $selectedOptionLabel.innerHTML;
            // remove data-glossary-init to init new glossary in option placeholder
            const $$glossaryItems = $optionPlaceholder.querySelectorAll('[data-glossary]');
            for (let i = 0; i < $$glossaryItems.length; i++) {
                $$glossaryItems[i].removeAttribute('data-glossary-init');
            }
            if (typeof this.onUpdate === 'function') {
                this.onUpdate($optionPlaceholder);
            }
        }
    }

    // iterate through the received steps data with their options and append them.
    // steps with only 1 option, will be automatically filled, displayed and
    // skipped without animation
    insertNewOptions (stepsAndOptions) {
        // collection of steps with their options which will be filled
        const steps = stepsAndOptions;

        // iterate through steps (often its just 1)
        for (let i = 0; i < steps.length; i++) {
            const $step = this.$$steps[this.clickedStepNumber + i];
            const $optionWrapper = $step.querySelector('[data-model-finder="options"]');
            // clear previous options
            $optionWrapper.innerHTML = '';
            const options = steps[i];

            // iterate through the options of the current step
            for (let j = 0; j < options.length; j++) {
                const option = options[j];
                $optionWrapper.appendChild(option);
            }

            // condition checks if we are in the last step from the ajax response (NOT FROM THE COMPLETE FILTER)
            // AND check if we are not finished
            if (i === steps.length - 1 && !this.isFinished) {
                $step.classList.remove('model-finder__step--disabled');
                $step.classList.remove('model-finder__step--filled');
                $step.classList.add('model-finder__step--active');
            } else {
                // we are in the last step of the filter and we are finished => it has only 1 option
                $step.classList.remove('model-finder__step--disabled');
                $step.classList.add('model-finder__step--filled');
                const stepNumber = parseInt($step.getAttribute('data-step-number'));
                this.setTextForOptionPlaceholder(stepNumber);
            }
        }
    }

    activateClickedStep ($step) {
        for (let i = 0; i < this.$$steps.length; i++) {
            const $currentStep = this.$$steps[i];

            // previous active step
            if ($currentStep.classList.contains('model-finder__step--active')) {
                $currentStep.classList.remove('model-finder__step--active');
                // previous active step can never be disabled, because it must
                // be clickable (which is determined with 'model-finder__step--filled')
                $currentStep.classList.add('model-finder__step--filled');
            }

            // set new active step
            if ($currentStep === $step) {
                $currentStep.classList.add('model-finder__step--active');
                $currentStep.classList.remove('model-finder__step--filled');
            }
        }
    }

    setPreFinishTextVisibility (isVisible) {
        if (isVisible) {
            this.$preFinishText.classList.remove('hidden');
        } else {
            this.$preFinishText.classList.add('hidden');
        }
    }

    setFinishTextVisibility (isVisible) {
        if (isVisible) {
            this.$finishText.classList.remove('hidden');

            // center finish text in viewport, but only on mobile, when visible
            if (window.innerWidth < this.breakpoints.l) {
                this.centerElementInViewport(this.$finishText);
            }
        } else {
            this.$finishText.classList.add('hidden');
        }
    }

    setResetButtonVisibility (isVisible) {
        if (isVisible) {
            this.$resetButton.classList.remove('hidden');
        } else {
            this.$resetButton.classList.add('hidden');
        }
    }

    resetFilter ($firstMeaningfulStep) {
        const indexFirstMeaningfulStep = $firstMeaningfulStep.getAttribute('data-step-number') - 1;
        // clear all selected options beginning from first meaningful step
        this.clearSelectedOptionPlaceholderRange(indexFirstMeaningfulStep);

        // clear checked input of first meaningful step
        const $firstStep = this.$$steps[indexFirstMeaningfulStep];
        const $$radios = $firstStep.querySelectorAll('[data-model-finder="radio"]');

        for (let i = 0; i < $$radios.length; i++) {
            const $radio = $$radios[i];
            $radio.checked = false;
        }

        // set first meaningful step active, previous steps filled and upcoming steps disabled
        for (let i = 0; i < this.$$steps.length; i++) {
            const $step = this.$$steps[i];

            // reset modifier classes
            $step.classList.remove('model-finder__step--active');
            $step.classList.remove('model-finder__step--filled');
            $step.classList.remove('model-finder__step--disabled');

            if (i < indexFirstMeaningfulStep) {
                $step.classList.add('model-finder__step--filled');
            } else if (i === indexFirstMeaningfulStep) {
                $step.classList.add('model-finder__step--active');
            } else {
                $step.classList.add('model-finder__step--disabled');
            }
        }
    }

    // clearing the selected option placeholder in a defined range of the steps
    // and disabled them, except the first one to prevent them to be clicked
    clearSelectedOptionPlaceholderRange (start = 0, end = this.$$steps.length - 1) {
        for (let i = start; i <= end; i++) {
            const $step = this.$$steps[i];
            const $placeholder = $step.querySelector('[data-model-finder="option-placeholder"]');
            $step.classList.remove('model-finder__step--filled');

            if (i > start) {
                $step.classList.add('model-finder__step--disabled');
            }

            if ($placeholder) {
                $placeholder.textContent = '';
            }
        }
    }

    scrollToStep ($step) {
        // wait for animation finish to calculate correct centered position,
        // because the elements height changes due to scaling of the step number
        if ($step) {
            setTimeout(() => {
                this.centerElementInViewport($step);
            }, 300);
        }
    }

    /**
     * ##################################################
     *
     * result (product table) related DOM manipulation
     *
     * ##################################################
     */

    setResultVisibility (isVisible) {
        const toggleState = () => {
            if (isVisible) {
                this.$modelFinder.classList.add('model-finder--active');
                this.unlockSlider();
            } else {
                this.$modelFinder.classList.remove('model-finder--active');
                this.resetResultScrollPosition();
                this.lockSlider();
            }

            this.handleStickybits();

            // reset slider position
            setTimeout(() => {
                this.slider.slideTo(0);
                this.slider.update();
            }, 100);
        };

        // if isVisible is undefined, just toggle to the opposite state
        if (isVisible === undefined) {
            isVisible = !this.$modelFinder.classList.contains('model-finder--active');
        }

        // prevent stage toggle, when the current state is the same as the incoming state,
        // otherwise mobile will always scroll up on step click, when it is result not active
        if (isVisible === this.$modelFinder.classList.contains('model-finder--active') ||
            !isVisible === !this.$modelFinder.classList.contains('model-finder--active')
        ) {
            return false;
        }

        // scroll up before toggle the active state to prevent a abruptly jump
        if ((window.innerWidth >= this.breakpoints.l && isVisible) || (window.innerWidth < this.breakpoints.l && !isVisible)) {
            this.scrollToTopOfElement(this.$modelFinder);

            setTimeout(() => {
                toggleState();
            }, 400);
        } else if (window.innerWidth < this.breakpoints.l && isVisible) {
            /**
             * activate result on mobile view
             * to prevent an abrupt jump or the result to vanish out of view due
             * to the position change. this is a animation fix
             * works as followed: scroll to relative position of the result,
             * make the css position switch
             * and then show result in full viewport
             */
            const resultBottomPos = getOffset(this.$filter).bottom;
            const yPos = resultBottomPos - window.innerHeight + this.$result.getBoundingClientRect().height;
            smoothScrollTo(0, yPos, 200);

            setTimeout(() => {
                toggleState();
            }, 200);

            setTimeout(() => {
                this.scrollToTopOfElement(this.$result);
            }, 550);
        } else {
            toggleState();
        }
    }

    initSlider () {
        const $slider = this.$result.querySelector('[data-model-finder="slider"]');
        const $prev = this.$result.querySelector('[data-model-finder="prev"]');
        const $next = this.$result.querySelector('[data-model-finder="next"]');

        if ($slider) {
            this.slider = new Swiper($slider, {
                slidesPerView: 'auto',
                allowTouchMove: false,
                watchSlidesVisibility: true, // needed for lazy loading
                lazy: {
                    loadPrevNext: true,
                    loadPrevNextAmount: 4,
                    loadOnTransitionStart: true
                },
                resistanceRatio: 0,
                navigation: {
                    prevEl: $prev,
                    nextEl: $next
                },
                observer: true, // needed because result width changes
                observeParents: true,
                freeMode: false,
                mousewheel: {
                    forceToAxis: true,
                    invert: true
                },
                simulateTouch: false
            });

            $slider.modelFinderSliderAPI = {
                update: () => {
                    this.slider.update();
                },
                setEqualHeight: () => {
                    this.setEqualHeight();
                }
            };
        }

        this.slider.on('slideNextTransitionStart', () => {
            this.modelFinderTracking.trackSliding(1);
        });

        this.slider.on('slidePrevTransitionStart', () => {
            this.modelFinderTracking.trackSliding(-1);
        });
    }

    // prevent slider to slide, when overlay is not active
    lockSlider () {
        if (this.slider) {
            this.slider.allowTouchMove = false;
            this.slider.mousewheel.disable();
        }
    }

    unlockSlider () {
        if (this.slider) {
            this.slider.allowTouchMove = true;
            this.slider.mousewheel.enable();
        }
    }

    renderResult (resultMarkup) {
        if (this.$result) {
            this.$result.innerHTML = resultMarkup;
        }
    }

    setEqualHeight () {
        const $$tableCells = this.$result.querySelectorAll('[data-height]');
        const cellHeightCollection = {};

        for (let i = 0; i < $$tableCells.length; i++) {
            const $tableCell = $$tableCells[i];
            // reset heights
            $tableCell.style.height = 'auto';
        }

        for (let i = 0; i < $$tableCells.length; i++) {
            const $tableCell = $$tableCells[i];
            const tableCellAttribute = $tableCell.getAttribute('data-height');
            const cellHeight = $tableCell.scrollHeight;

            // add not existing keys to height object collection
            const hasAttribute = Object.prototype.hasOwnProperty.call(cellHeightCollection, tableCellAttribute);

            if (!hasAttribute) {
                cellHeightCollection[tableCellAttribute] = cellHeight;
            } else {
                const maxHeight = cellHeightCollection[tableCellAttribute];
                if (cellHeight > maxHeight) {
                    // update new max height cell
                    cellHeightCollection[tableCellAttribute] = cellHeight;
                }
            }
        }

        // set max height to DOM elements
        for (let i = 0; i < $$tableCells.length; i++) {
            const $tableCell = $$tableCells[i];
            const tableCellAttribute = $tableCell.getAttribute('data-height');
            const height = cellHeightCollection[tableCellAttribute];
            $tableCell.style.height = height + 'px';
        }
    }

    resetResultScrollPosition () {
        // supported by IE11
        this.$result.scrollTop = 0;
        this.$result.scrollLeft = 0;
    }

    setResultLoadingAnimation (isVisible) {
        if (isVisible) {
            this.$result.classList.add('model-finder__result--loading');
        } else {
            this.$result.classList.remove('model-finder__result--loading');
        }
    }

    /**
     * ##################################################
     *
     * custom helper functions
     *
     * ##################################################
     */

    // scrolls to top of module, without the padding, but with optional tabnav
    // and nav height, when visible
    scrollToTopOfElement ($element) {
        window.headerMovement = false;
        const elementOffset = getOffset($element);
        const top = Math.ceil(elementOffset.top);
        const paddingTop = Math.ceil(parseFloat(window.getComputedStyle($element, null).getPropertyValue('padding-top')));

        const $header = document.querySelector('[data-header="root"]');
        let headerHeight = 0;
        if ($header) {
            if ($header.classList.contains('is--visible')) {
                headerHeight = $header.getBoundingClientRect().height;
            }
        }

        const $tabnav = document.querySelector('[data-tabnav="root"]');
        let tabnavHeight = 0;
        if ($tabnav) {
            tabnavHeight = $tabnav.getBoundingClientRect().height;
        }

        // custom threshold when element is module finder root
        let customDistance = 0;
        if ($element === this.$modelFinder) {
            if (window.innerWidth < this.breakpoints.l) {
                customDistance = 50;
            } else {
                // 5px error handling on smaller devices
                customDistance = 5;
            }
        }

        const scrollTopPos = top + paddingTop - tabnavHeight - headerHeight + customDistance;
        smoothScrollTo(0, scrollTopPos);
        setTimeout(() => {
            window.headerMovement = true;
        }, 700);
    }

    /**
     * scroll the given DOM Element to the center of the viewport (y-axis only)
     * @param {DOM Node/Element} $element
     */
    centerElementInViewport ($element) {
        const elementHeight = $element.getBoundingClientRect().height;
        const elementOffset = getOffset($element);
        const top = elementOffset.top + elementHeight / 2 - window.innerHeight / 2;
        smoothScrollTo(0, top);
    }
}

export { ModelFinderView };
